// Asynchronous work owned by the state that asked for it.
//
// - A `Scope` is a node in a tree of AbortSignals: cancelling one cancels
//   everything under it. Work started for a page, a pull request or a
//   signed-in account lives in that one's scope.
// - A `Runner` runs work in named lanes. Starting work in a lane supersedes
//   (aborts) the work there before; an answer is delivered only while its
//   run is still the lane's newest and nothing cancelled it. Every run is
//   tagged with a token the runner alone compares, so no caller keeps a
//   generation counter, and a late answer cannot land in newer state.
// - A `Scheduled` retry exists only because a runner armed its timer: the
//   runtime alone makes one (`Runner.schedule`, the `retry` effect), and it
//   says whether that timer is still pending. A view can therefore promise
//   "Changeling will try again by itself" only for a retry that will run.
// - A `Program` is a store driven by messages: `update(state, message)`
//   gives the next state and the effects to run, as data (`task`, `after`,
//   `retry`, `cancel`, and `port`: something only the program's owner can
//   do, such as touching the page, handed to it as a value). It follows
//   the Svelte store contract, and resets to
//   its initial state, dropping everything in flight, when the account
//   changes. Each reset starts a new epoch: what an update of an earlier
//   epoch returned is neither applied nor run.
//
// Work is any function of an AbortSignal that returns a promise (a fetch,
// the GitHub scheduler, an IndexedDB read). Superseding a lane aborts the
// signal the runtime passed to that run; a shared request behind it (the
// GitHub scheduler's de-duplication) must treat that as one waiter leaving,
// not as cancelling the request for every waiter.

/** What a run produced: its value, or its error (`E`: a typed failure, or `unknown` for whatever was thrown). */
export type Outcome<T, E = unknown> = { ok: true; value: T } | { ok: false; error: E };

/** Work that stops when `signal` aborts (or whose answer is dropped then). */
export type Work<T> = (signal: AbortSignal) => Promise<T>;

/** A node in a tree of AbortSignals. */
export class Scope {
  private readonly controller = new AbortController();
  private readonly kids = new Set<Scope>();
  private parent: Scope | null;
  /** Removes the listener on the external signal a root follows. */
  private unfollow: (() => void) | null = null;

  private constructor(parent: Scope | null) {
    this.parent = parent;
  }

  /** A scope with no parent; with `signal`, cancelled when it aborts. */
  static root(signal?: AbortSignal): Scope {
    const s = new Scope(null);
    if (!signal) return s;
    if (signal.aborted) {
      s.cancel(signal.reason);
      return s;
    }
    const onAbort = () => s.cancel(signal.reason);
    signal.addEventListener('abort', onAbort, { once: true });
    s.unfollow = () => signal.removeEventListener('abort', onAbort);
    return s;
  }

  get signal(): AbortSignal {
    return this.controller.signal;
  }

  get cancelled(): boolean {
    return this.controller.signal.aborted;
  }

  /** How many children are running (cancelled ones are let go). */
  get children(): number {
    return this.kids.size;
  }

  /** A scope cancelled with this one; it starts cancelled when this one is. */
  child(): Scope {
    const c = new Scope(this);
    if (this.cancelled) c.cancel(this.signal.reason);
    else this.kids.add(c);
    return c;
  }

  /** Abort this scope's signal and every descendant's, with `reason`, and let go of the signal it follows. */
  cancel(reason?: unknown): void {
    if (!this.cancelled) this.controller.abort(reason);
    this.unfollow?.();
    this.unfollow = null;
    this.parent?.kids.delete(this);
    this.parent = null;
    const kids = [...this.kids];
    this.kids.clear();
    for (const k of kids) k.cancel(this.signal.reason);
  }
}

/** A retry the user tries themselves. */
export type Manual = { readonly kind: 'manual' };
export const manual: Manual = Object.freeze({ kind: 'manual' });

let mint: (at: number, lane: string, live: () => boolean) => Scheduled;

/**
 * A retry the runtime armed: at `at` (epoch ms), in `lane`. Only a runner
 * makes one, as it arms the timer (`Runner.schedule`, the `retry` effect).
 * `pending` is false once the timer fired, or was superseded, cancelled or
 * reset: from then on nothing will retry by itself.
 */
export class Scheduled {
  readonly kind = 'scheduled' as const;
  readonly #live: () => boolean;

  private constructor(
    readonly at: number,
    readonly lane: string,
    live: () => boolean,
  ) {
    this.#live = live;
  }

  static {
    mint = (at, lane, live) => new Scheduled(at, lane, live);
  }

  get pending(): boolean {
    return this.#live();
  }
}

/** Whether a failed load is tried again by itself (`Scheduled`), or only when the user asks (`Manual`). */
export type Retry = Scheduled | Manual;

/** The time a retry runs by itself, or null when none is pending. */
export function pendingAt(r: Retry): number | null {
  return r instanceof Scheduled && r.pending ? r.at : null;
}

interface Slot {
  token: number;
  scope: Scope;
}

/** Runs work in lanes, delivering only answers that are still current (see the file comment). */
export class Runner {
  private scope: Scope;
  private readonly lanes = new Map<string, Slot>();
  private token = 0;
  /** Runs whose work has not settled yet (superseded ones too), and who waits for there to be none. */
  private running = 0;
  private idle: (() => void)[] = [];

  /** Work runs under `parent`: cancelling it stops the runner for good. */
  constructor(
    private readonly parent: Scope = Scope.root(),
    private readonly now: () => number = Date.now,
  ) {
    this.scope = parent.child();
  }

  /**
   * Start `work` in `lane`, superseding what runs there, and pass its
   * outcome to `deliver` if it is still current when it settles; a thrown
   * error becomes `errorOf(thrown)`. Under a cancelled scope, nothing
   * starts. The run's scope ends once delivered.
   */
  run<T, E = unknown>(lane: string, work: Work<T>, deliver: (o: Outcome<T, E>) => void, errorOf: (thrown: unknown) => E = (e) => e as E): void {
    this.runOutcome(lane, async (signal) => ({ ok: true, value: await work(signal) }) as Outcome<T, E>, deliver, errorOf);
  }

  /** `run` for work that already answers with an outcome (the GitHub scheduler's `Result`). */
  runOutcome<T, E>(lane: string, work: Work<Outcome<T, E>>, deliver: (o: Outcome<T, E>) => void, errorOf: (thrown: unknown) => E): void {
    const slot = this.claim(lane);
    if (!slot) return;
    let p: Promise<Outcome<T, E>>;
    try {
      p = work(slot.scope.signal);
    } catch (error) {
      p = Promise.reject(error);
    }
    this.running++;
    p.then(
      (o) => o,
      (thrown): Outcome<T, E> => ({ ok: false, error: errorOf(thrown) }),
    ).then((o) => {
      try {
        if (this.release(lane, slot)) deliver(o);
      } finally {
        // A run its delivery started counts before this one stops counting.
        if (--this.running === 0) for (const r of this.idle.splice(0)) r();
      }
    });
  }

  /**
   * Resolves once every run started so far has settled and its answer was
   * delivered or dropped, and so has every run those deliveries started.
   * Superseded, cancelled and reset runs count until their work settles.
   * Timers (`after`, `schedule`) are not waited for. For tests, and for
   * whatever must know the runner has nothing in flight.
   */
  settled(): Promise<void> {
    return this.running === 0 ? Promise.resolve() : new Promise((r) => this.idle.push(r));
  }

  /** Call `fire` after `ms`, unless `lane` is superseded, cancelled or reset first. */
  after(lane: string, ms: number, fire: () => void): void {
    this.arm(lane, ms, fire);
  }

  /**
   * Call `fire` at `at` (epoch ms), as `after` does, and return the
   * `Scheduled` retry that stands for it; null under a cancelled scope.
   * `lapsed` is called if it stops being pending without firing because
   * the lane was superseded or cancelled (not on reset or dispose, which
   * drop the state that showed it).
   */
  schedule(lane: string, at: number, fire: (s: Scheduled) => void, lapsed?: (s: Scheduled) => void): Scheduled | null {
    let token: Scheduled | null = null;
    const slot = this.arm(lane, Math.max(0, at - this.now()), () => fire(token!));
    if (!slot) return null;
    token = mint(at, lane, () => this.lanes.get(lane)?.token === slot.token && !slot.scope.cancelled);
    const signal = slot.scope.signal;
    if (lapsed) signal.addEventListener('abort', () => signal.reason === 'cancelled' && lapsed(token!), { once: true });
    return token;
  }

  /** Whether `lane` has work or a timer pending. */
  pending(lane: string): boolean {
    return this.lanes.has(lane);
  }

  /** Stop what runs in `lane`; its answer is dropped. */
  cancel(lane: string): void {
    this.lanes.get(lane)?.scope.cancel('cancelled');
    this.lanes.delete(lane);
  }

  /** Drop and abort everything in flight (another account signed in); later runs proceed. */
  reset(): void {
    this.scope.cancel('reset');
    this.lanes.clear();
    this.scope = this.parent.child();
  }

  /** Drop and abort everything, and start nothing from now on. */
  dispose(): void {
    this.scope.cancel('disposed');
    this.lanes.clear();
  }

  private arm(lane: string, ms: number, fire: () => void): Slot | null {
    const slot = this.claim(lane);
    if (!slot) return null;
    const t = setTimeout(() => {
      if (this.release(lane, slot)) fire();
    }, ms);
    slot.scope.signal.addEventListener('abort', () => clearTimeout(t), { once: true });
    return slot;
  }

  private claim(lane: string): Slot | null {
    this.cancel(lane);
    if (this.scope.cancelled) return null;
    const slot = { token: ++this.token, scope: this.scope.child() };
    this.lanes.set(lane, slot);
    return slot;
  }

  /** Whether `slot` is still `lane`'s current run; if so, it ends. */
  private release(lane: string, slot: Slot): boolean {
    if (this.lanes.get(lane)?.token !== slot.token || slot.scope.cancelled) return false;
    this.lanes.delete(lane);
    slot.scope.cancel('done');
    return true;
  }
}

/** Something for the runtime to do, as data. `Out` is what the program hands its owner (`port`). */
export type Effect<Msg, Out = never> =
  | {
      readonly kind: 'task';
      readonly lane: string;
      readonly work: Work<Outcome<unknown, unknown>>;
      readonly then: (o: Outcome<unknown, unknown>) => Msg;
      readonly errorOf: (thrown: unknown) => unknown;
    }
  | { readonly kind: 'after'; readonly lane: string; readonly ms: number; readonly msg: Msg }
  | { readonly kind: 'retry'; readonly lane: string; readonly at: number; readonly on: RetryMessages<Msg> }
  | { readonly kind: 'cancel'; readonly lane: string }
  | { readonly kind: 'port'; readonly value: Out };

/**
 * Run `work` in `lane` (superseding what runs there); its outcome comes
 * back as `then(outcome)`. With `errorOf`, a thrown error is typed by it.
 */
export function task<T, Msg>(lane: string, work: Work<T>, then: (o: Outcome<T>) => Msg): Effect<Msg>;
export function task<T, E, Msg>(lane: string, work: Work<T>, then: (o: Outcome<T, E>) => Msg, errorOf: (thrown: unknown) => E): Effect<Msg>;
export function task<T, E, Msg>(lane: string, work: Work<T>, then: (o: Outcome<T, E>) => Msg, errorOf: (thrown: unknown) => E = (e) => e as E): Effect<Msg> {
  return taskOutcome(lane, async (s) => ({ ok: true, value: await work(s) }), then, errorOf);
}

/** `task` for work that answers with an outcome itself (the GitHub scheduler's `Result`); a throw is typed by `errorOf`. */
export function taskOutcome<T, E, Msg>(
  lane: string,
  work: Work<Outcome<T, E>>,
  then: (o: Outcome<T, E>) => Msg,
  errorOf: (thrown: unknown) => E,
): Effect<Msg> {
  return {
    kind: 'task',
    lane,
    work: work as Work<Outcome<unknown, unknown>>,
    then: then as (o: Outcome<unknown, unknown>) => Msg,
    errorOf,
  };
}

/** Send `msg` after `ms`, unless `lane` is superseded first (a debounce). */
export function after<Msg>(lane: string, ms: number, msg: Msg): Effect<Msg> {
  return { kind: 'after', lane, ms, msg };
}

/**
 * The messages of a retry's life, each with the `Scheduled` it concerns:
 * - `armed`, at once: the state shows it (it promises a retry by itself);
 * - `fire`, at its time: the state moves on (the retry runs);
 * - `lapsed`, when the lane is superseded or cancelled first: the state
 *   stops promising it (`remote.ts` `settle`).
 * So the state changes when a retry's time comes or it is dropped, without
 * a view re-reading anything.
 */
export interface RetryMessages<Msg> {
  armed: (s: Scheduled) => Msg;
  fire: (s: Scheduled) => Msg;
  lapsed: (s: Scheduled) => Msg;
}

/** Arm a retry at `at` (epoch ms) in `lane`; see `RetryMessages`. */
export function retry<Msg>(lane: string, at: number, on: RetryMessages<Msg>): Effect<Msg> {
  return { kind: 'retry', lane, at, on };
}

/** Stop what runs in `lane`. */
export function cancel(lane: string): Effect<never> {
  return { kind: 'cancel', lane };
}

/**
 * Hand `value` to the program's owner (`ProgramOptions.port`), which
 * carries it out: what an update asks of the world outside the runtime
 * (the page, another store), still as data.
 */
export function port<Out>(value: Out): Effect<never, Out> {
  return { kind: 'port', value };
}

/** A state and the effects to run next. */
export type Next<S, Msg, Out = never> = readonly [S, (readonly Effect<Msg, Out>[])?];

export interface ProgramOptions<S, Msg, Out = never> {
  /** The state to start from, and on every reset. */
  init: () => Next<S, Msg, Out>;
  update: (state: S, msg: Msg) => Next<S, Msg, Out>;
  /** Carries out a `port` effect, in the step that returned it and in order. Without it such effects are dropped. */
  port?: (value: Out) => void;
  /**
   * Subscribes to what resets the program (an identity change: see
   * api/identity.ts `onIdentityChange`); returns the unsubscribe.
   */
  resetOn?: (listener: () => void) => () => void;
  /** The scope the program's work runs under. */
  scope?: Scope;
  now?: () => number;
}

/** Rejects a pending `until` when its program is disposed. */
export class ProgramDisposed extends Error {
  constructor() {
    super('the program was disposed');
    this.name = 'ProgramDisposed';
  }
}

/** A message-driven store (see the file comment). */
export class Program<S, Msg, Out = never> {
  private current: S;
  private readonly runner: Runner;
  private readonly subscribers = new Set<(s: S) => void>();
  private readonly waiting = new Set<(e: Error) => void>();
  private readonly queue: Msg[] = [];
  private draining = false;
  private disposed = false;
  /** Counts resets: work of an earlier epoch is neither applied nor run. */
  private epoch = 0;
  private readonly unhook: (() => void) | null;

  constructor(private readonly opts: ProgramOptions<S, Msg, Out>) {
    this.runner = new Runner(opts.scope, opts.now);
    const [s, fx] = opts.init();
    this.current = s;
    this.unhook = opts.resetOn?.(() => this.reset()) ?? null;
    this.perform(fx, this.epoch);
  }

  get state(): S {
    return this.current;
  }

  /** Svelte's store contract: `run` hears the state now and after each change. */
  subscribe(run: (s: S) => void): () => void {
    this.subscribers.add(run);
    run(this.current);
    return () => this.subscribers.delete(run);
  }

  /**
   * Handle `msg`; one sent while another is handled is handled after it.
   * An update that throws is logged and skipped; the queue goes on.
   */
  send(msg: Msg): void {
    if (this.disposed) return;
    this.queue.push(msg);
    if (this.draining) return;
    this.draining = true;
    try {
      while (this.queue.length && !this.disposed) {
        const m = this.queue.shift() as Msg;
        const epoch = this.epoch;
        let next: Next<S, Msg, Out>;
        try {
          next = this.opts.update(this.current, m);
        } catch (e) {
          console.error('Changeling: a state update failed; the message is dropped.', e);
          continue;
        }
        // A reset during the update (or while subscribers heard it) belongs to a newer epoch.
        if (epoch !== this.epoch) continue;
        this.set(next[0]);
        this.perform(next[1], epoch);
      }
    } finally {
      this.draining = false;
    }
  }

  /** Start over from `init`, dropping and aborting everything in flight and every queued message. */
  reset(): void {
    if (this.disposed) return;
    this.epoch++;
    this.runner.reset();
    this.queue.length = 0;
    const epoch = this.epoch;
    const [s, fx] = this.opts.init();
    this.set(s);
    this.perform(fx, epoch);
  }

  /** Resolves once the work the program started has settled and been handled (see `Runner.settled`). */
  settled(): Promise<void> {
    return this.runner.settled();
  }

  /** Resolves with the state once `pred` holds of it (at once if it does now); rejects with ProgramDisposed on dispose. */
  until(pred: (s: S) => boolean): Promise<S> {
    if (this.disposed) return Promise.reject(new ProgramDisposed());
    if (pred(this.current)) return Promise.resolve(this.current);
    return new Promise((resolve, reject) => {
      const fail = (e: Error) => {
        stop();
        reject(e);
      };
      const stop = () => {
        this.subscribers.delete(hear);
        this.waiting.delete(fail);
      };
      const hear = (s: S) => {
        if (!pred(s)) return;
        stop();
        resolve(s);
      };
      this.subscribers.add(hear);
      this.waiting.add(fail);
    });
  }

  /** Stop: nothing runs, is delivered, or is handled from now on; pending `until`s reject. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.unhook?.();
    this.runner.dispose();
    this.queue.length = 0;
    for (const fail of [...this.waiting]) fail(new ProgramDisposed());
  }

  private set(s: S) {
    if (Object.is(s, this.current)) return;
    this.current = s;
    for (const run of [...this.subscribers]) run(s);
  }

  private perform(fx: readonly Effect<Msg, Out>[] | undefined, epoch: number) {
    for (const e of fx ?? []) {
      if (this.disposed || epoch !== this.epoch) return;
      switch (e.kind) {
        case 'task':
          this.runner.runOutcome(e.lane, e.work, (o) => this.send(e.then(o)), e.errorOf);
          break;
        case 'after':
          this.runner.after(e.lane, e.ms, () => this.send(e.msg));
          break;
        case 'retry': {
          const s = this.runner.schedule(
            e.lane,
            e.at,
            (sc) => this.send(e.on.fire(sc)),
            (sc) => this.send(e.on.lapsed(sc)),
          );
          if (s) this.send(e.on.armed(s));
          break;
        }
        case 'cancel':
          this.runner.cancel(e.lane);
          break;
        case 'port':
          try {
            this.opts.port?.(e.value);
          } catch (error) {
            console.error('Changeling: an effect failed; the rest go on.', error);
          }
          break;
      }
    }
  }
}
